/**
 * Pause / play control for the announcement bar.
 *
 * WCAG 2.1.1 SC 2.2.2 (Pause, Stop, Hide) requires a mechanism to stop any
 * motion that starts automatically, lasts more than five seconds, and runs
 * alongside other content. The announcement bar does exactly that in both of
 * its animated layouts:
 *
 *   - "marquee"  a CSS animation with animation-iteration-count: infinite
 *   - "slider"   a Flickity autoplay driven by data-slider-speed
 *
 * This control covers both. It also seeds itself to the paused state when the
 * visitor has asked for reduced motion, so those users land on a stopped bar
 * and can start it themselves if they want to.
 */
(function () {
  'use strict';

  var PAUSED_CLASS = 'announcement--paused';
  var STORAGE_KEY = 'avf:motion-paused';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Remembered choice wins; otherwise fall back to the motion preference. */
  function initialPausedState() {
    try {
      var stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (stored === 'true') return true;
      if (stored === 'false') return false;
    } catch (e) {
      // Private browsing or blocked storage: fall through to the media query.
    }
    return prefersReducedMotion();
  }

  function remember(paused) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, paused ? 'true' : 'false');
    } catch (e) {
      // Nothing to do; the control still works for this page view.
    }
  }

  /** Flickity's autoplay is JS-driven, so the CSS class alone will not stop it. */
  function setSliderPlaying(bar, playing) {
    var slider = bar.querySelector('[data-slider]');
    if (!slider || !window.theme || !window.theme.Flickity) return;

    var instance = window.theme.Flickity.data(slider);
    if (!instance) return;

    if (playing && typeof instance.unpausePlayer === 'function') {
      instance.unpausePlayer();
    } else if (!playing && typeof instance.pausePlayer === 'function') {
      instance.pausePlayer();
    }
  }

  function apply(bar, button, paused) {
    bar.classList.toggle(PAUSED_CLASS, paused);
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
    setSliderPlaying(bar, !paused);
  }

  function setup(button) {
    if (button.dataset.motionToggleBound) return;
    button.dataset.motionToggleBound = 'true';

    var bar = button.closest('announcement-bar');
    if (!bar) return;

    apply(bar, button, initialPausedState());

    button.addEventListener('click', function () {
      var paused = !bar.classList.contains(PAUSED_CLASS);
      apply(bar, button, paused);
      remember(paused);
    });
  }

  /* ----------------------------------------------------------
   * Autoplaying background video
   *
   * Same criterion, different mechanism. These videos are muted,
   * looping, and control-less by design. Native <video controls> would
   * paint a full control bar across a full-bleed hero, and two of the
   * four render inside a <template> that only becomes real DOM after
   * the theme's video component runs — so the button is injected here
   * instead, matching the announcement bar control.
   * -------------------------------------------------------- */

  var VIDEO_SELECTOR = 'video[autoplay][loop]';

  function labelStrings() {
    // Reuse the announcement bar's translated labels when one is on the page;
    // otherwise fall back to the document language's own <video> defaults.
    var source = document.querySelector('[data-motion-toggle]');
    return {
      pause: (source && source.dataset.labelPause) || 'Pause animation',
      play: (source && source.dataset.labelPlay) || 'Play animation'
    };
  }

  function decorateVideo(video) {
    if (video.dataset.motionControlBound) return;
    if (!video.hasAttribute('muted') && !video.muted) return; // audio videos already ship controls
    video.dataset.motionControlBound = 'true';

    var labels = labelStrings();
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'video__motion-toggle';
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-label', labels.pause);
    button.innerHTML =
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="currentColor" class="icon icon-motion-pause">' +
      '<rect x="3.5" y="2.5" width="3" height="11" rx="0.5"/><rect x="9.5" y="2.5" width="3" height="11" rx="0.5"/></svg>' +
      '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="currentColor" class="icon icon-motion-play">' +
      '<path d="M4.5 2.7a.5.5 0 0 1 .76-.43l8 5.3a.5.5 0 0 1 0 .86l-8 5.3a.5.5 0 0 1-.76-.43V2.7Z"/></svg>';

    function sync(paused) {
      button.setAttribute('aria-pressed', paused ? 'true' : 'false');
      button.setAttribute('aria-label', paused ? labels.play : labels.pause);
    }

    button.addEventListener('click', function () {
      if (video.paused) {
        var playing = video.play();
        if (playing && typeof playing.catch === 'function') playing.catch(function () {});
        sync(false);
      } else {
        video.pause();
        sync(true);
      }
    });

    // Keep the button honest if anything else pauses the video.
    video.addEventListener('pause', function () { sync(true); });
    video.addEventListener('play', function () { sync(false); });

    var host = video.parentElement;
    if (host) {
      // The button is positioned against this element; make sure it can anchor.
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(button);
    }

    if (prefersReducedMotion()) {
      video.pause();
      sync(true);
    }
  }

  function scanVideos() {
    document.querySelectorAll(VIDEO_SELECTOR).forEach(decorateVideo);
  }

  function scan() {
    document.querySelectorAll('[data-motion-toggle]').forEach(setup);
    scanVideos();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Videos inside <template data-video-template> are cloned into the page
  // lazily, so watch for them rather than scanning once.
  if ('MutationObserver' in window) {
    new MutationObserver(scanVideos).observe(document.body, { childList: true, subtree: true });
  }

  // Flickity initialises after this script runs, and the theme editor
  // re-renders sections in place, so re-apply once both have settled.
  window.addEventListener('load', function () {
    document.querySelectorAll('[data-motion-toggle]').forEach(function (button) {
      var bar = button.closest('announcement-bar');
      if (bar) setSliderPlaying(bar, !bar.classList.contains(PAUSED_CLASS));
    });
  });

  document.addEventListener('shopify:section:load', scan);
})();
